// Import the RTK Query methods from the React-specific entry point
import {
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from "@reduxjs/toolkit/query/react"
import { RTK_SLICE_API_LIST } from "../../common/constants"
import { stripLegacySubscriptionConfigKeys } from "../../common/utils/subscription-config"
import { setSessionExpired } from "../slices/auth"

const axiosBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_HOST ?? "/",
  credentials: "include",
  prepareHeaders: (headers: Headers, { getState }: any) => {
    const token = getState().auth.user?.sessid
    if (token) {
      headers.set("Authorization", token)
    }
    return headers
  }
})

/** How long a confirmed-valid session answers the "not allowed" check without a new probe. */
const SESSION_PROBE_TTL_MS = 60_000

let sessionProbe: { startedAt: number; isValid: Promise<boolean> } | null = null

/**
 * One /api/v1/me probe shared by every "not allowed" response. A page whose
 * queries are all denied used to send one probe per denied response, which
 * turned a single permission error into a request storm. A failed probe is
 * not cached: it marks the session expired, and that flag stops further probes.
 */
const isSessionStillValid = (
  api: Parameters<typeof axiosBaseQuery>[1],
  extraOptions: Parameters<typeof axiosBaseQuery>[2]
): Promise<boolean> => {
  if (
    sessionProbe &&
    Date.now() - sessionProbe.startedAt < SESSION_PROBE_TTL_MS
  ) {
    return sessionProbe.isValid
  }
  const probe = {
    startedAt: Date.now(),
    isValid: Promise.resolve(
      axiosBaseQuery("/api/v1/me", api, extraOptions)
    ).then(meResult => {
      const isValid =
        !!meResult.data && (meResult.data as any)?.status !== "error"
      if (!isValid && sessionProbe === probe) sessionProbe = null
      return isValid
    })
  }
  sessionProbe = probe
  return probe.isValid
}

export const resetSessionProbe = () => {
  sessionProbe = null
}

const cleanSubscriptionConfigData = (data: any): any => {
  if (!data?.config || typeof data.config !== "object") {
    return data
  }

  return {
    ...data,
    config: stripLegacySubscriptionConfigKeys(data.config)
  }
}

export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await axiosBaseQuery(args, api, extraOptions)

  const url = typeof args === "string" ? args : args.url

  if (url?.includes("subscription-config") && result.data) {
    if (Array.isArray(result.data)) {
      result.data = result.data.map(cleanSubscriptionConfigData)
    } else if (typeof result.data === "object") {
      result.data = cleanSubscriptionConfigData(result.data)
    }
  }

  if (
    result.data &&
    (result.data as any)?.status === "error" &&
    (result.data as any)?.message === "not allowed to do that"
  ) {
    const currentPath = window.location.pathname
    const alreadyExpired = (api.getState() as any)?.auth?.isSessionExpired
    if (!currentPath.startsWith("/user/login") && !alreadyExpired) {
      // A denial can be a real permission error or a dead session; /api/v1/me
      // tells them apart.
      if (!(await isSessionStillValid(api, extraOptions))) {
        api.dispatch(setSessionExpired(true))
      }
    }
  }

  return result
}

export const getTags = (reducerPath: string, key: string, result?: any[]) => [
  ...(result ?? []).map(
    item =>
      ({
        type: reducerPath as any,
        id: item[key]
      } as const)
  ),
  { type: reducerPath as any, id: RTK_SLICE_API_LIST }
]
